import React from "react";
import { icons } from "@kernel/icon-set-essentials";
import {
  HttpError,
  HttpNotFoundError,
  IBlocSyncInteractor,
  isHttpError,
  isHttpNotFoundError,
  isRemoteValidationError,
  RemoteValidationError
} from "@kernel/frontend-react-app";
import { IconContextProvider, IconProps } from "@kernel/react-icons";
import {
  INotificationHandler,
  Notificator,
  ToastPosition
} from "@kernel/react-ui-kit";
import {
  HttpErrorInteractor,
  HttpErrorProps,
  HttpNotFoundErrorInteractor,
  HttpNotFoundErrorPorps,
  RemoteValidationErrorInteractor,
  RemoteValidationErrorPorps
} from "../interactors";
import {
  HttpErrorToast,
  HttpNotFoundErrorToast,
  RemoteValidationErrorToast
} from "./toast";

type TErrorInteractors = {
  httpError: IBlocSyncInteractor<HttpErrorProps, string>;
  httpNotFoundError: IBlocSyncInteractor<HttpNotFoundErrorPorps, string>;
  remoteValidationError: IBlocSyncInteractor<
    RemoteValidationErrorPorps,
    string
  >;
};

export class HttpErrorNotificationHandler implements INotificationHandler {
  private readonly notificator: Notificator;
  private readonly isDevelopment: boolean;
  public icon: IconProps;

  constructor(position: ToastPosition, private interactors: TErrorInteractors) {
    this.notificator = Notificator.create(
      "error",
      {
        position,
        className: "pmmon__app-toaster-error"
      },
      (chidren) => (
        <IconContextProvider icons={icons}>{chidren}</IconContextProvider>
      )
    );
    this.isDevelopment = import.meta.env.MODE === "dev";
    this.icon = {
      set: "fa",
      icon: ["solid", "exclamation-triangle"]
    };
  }

  public handle(err: unknown) {
    if (this.isDevelopment) console.error(err);
    if (isHttpError(err as HttpError)) {
      return this.showHttpError(err as HttpError);
    }
    if (isHttpNotFoundError(err as HttpNotFoundError)) {
      return this.showHttpNotFoundError(err as HttpNotFoundError);
    }
    if (isRemoteValidationError(err as RemoteValidationError)) {
      return this.showRemoteValidationError(err as RemoteValidationError);
    }
    if (!this.isDevelopment) return;
    throw err;
  }

  private showHttpError(err: HttpError) {
    const error = this.interactors.httpError.execute({ err });
    const toast = new HttpErrorToast(err, error).toast();
    this.notificator.show(toast);
  }

  private showHttpNotFoundError(err: HttpNotFoundError) {
    const error = this.interactors.httpNotFoundError.execute({ err });
    const toast = new HttpNotFoundErrorToast(error).toast();
    this.notificator.show(toast);
  }

  private showRemoteValidationError(err: RemoteValidationError) {
    const error = this.interactors.remoteValidationError.execute({ err });
    const toast = new RemoteValidationErrorToast(error).toast();
    this.notificator.show(toast);
  }

  public static create(position: ToastPosition = "top-end") {
    const interactors: TErrorInteractors = {
      httpError: HttpErrorInteractor.create(),
      httpNotFoundError: HttpNotFoundErrorInteractor.create(),
      remoteValidationError: RemoteValidationErrorInteractor.create()
    };
    return new HttpErrorNotificationHandler(position, interactors);
  }
}
